import * as React from "react";
import { Card, Box, Button, Grid, IconButton, Typography, ListItemText, Divider, CardContent, List, ListItem } from "@mui/material";
import { toast } from "react-toastify";
import { MdLocationPin } from 'react-icons/md';
import { useHistory } from "react-router-dom";
import { findUserId } from "../app/services/UtilServices";
import { addAAddress, updateAAddress, deleteAAddress, searchAddress } from "../app/services/address/addressAPI";
import { Radio, RadioGroup } from "@mui/material";
import { CircularProgress } from "@mui/material";
import { IStandardAPIResponse } from "definitions/resp";
import AddressForm from "../pages/Addressform";

interface IAddressCardProps {
    address: IUserAddress;
    onDelete: (address: IUserAddress, index: number) => void;
    onEdit: (address: IUserAddress) => void;
    onDefaultChecked: (address: IUserAddress) => void;
}

interface IUserAddress {
    _id?: string;
    userId: string;
    name: string;
    phone: string;
    pincode: string;
    locality: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    landmark?: string;
    alternatePhone?: string;
    addressType: 'home' | 'work';
    isDefaultAddress: boolean;
    location?: {
        type: "Point";
        coordinates: [number, number];
    }
}

// IAddressResponse now correctly inherits types from IStandardAPIResponse
interface IAddressResponse extends IStandardAPIResponse<IUserAddress[]> { }

interface IAddressPageProps {
    setIsAdressUpdated?: (value: boolean) => void;
}


const AddressCard: React.FC<IAddressCardProps> = ({ address, onDelete, onEdit, onDefaultChecked }) => {
    return (
        <Card sx={{ mb: 2, position: 'relative' }}>
            {address.isDefaultAddress && (
                <Box sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bgcolor: 'grey.800',
                    px: 1,
                    py: 0.5,
                    borderBottomLeftRadius: 1
                }}>
                    <Typography color="white">Default</Typography>
                </Box>
            )}

            <CardContent>
                <Grid container spacing={2}>
                    <Grid item>
                        <Radio
                            checked={address.isDefaultAddress}
                            onChange={() => onDefaultChecked(address)}
                        />
                    </Grid>
                    <Grid item xs>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6">{address.name}</Typography>
                            <Box sx={{ bgcolor: 'grey.300', px: 1, py: 0.5 }}>
                                <Typography variant="caption">
                                    {address.addressType === 'home' ? 'Home' : 'Work'}
                                </Typography>
                            </Box>
                        </Box>
                        <Typography>
                            {`${address.addressLine1}, 
                ${address.addressLine2 ? address.addressLine2 + ', ' : ''} 
                ${address.landmark ? address.landmark + ', ' : ''} 
                ${address.city}, ${address.state} - ${address.pincode}`}
                        </Typography>
                        <Typography>{address.phone}</Typography>
                    </Grid>
                </Grid>
            </CardContent>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
                <Button color="primary" onClick={() => onDelete(address, 0)}>Delete</Button>
                <Button color="primary" onClick={() => onEdit(address)}>Edit</Button>
            </Box>
        </Card>
    );
}
const AddressPage: React.FC<IAddressPageProps> = ({ setIsAdressUpdated }) => {
    const [addresses, setAddresses] = React.useState<IUserAddress[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [showAddressForm, setShowAddressForm] = React.useState(false);
    const [selectedAddress, setSelectedAddress] = React.useState<IUserAddress | null>(null);

    const handleAddressFormClose = () => {
        setShowAddressForm(false);
        setSelectedAddress(null);
    };

    const handleAddressClick = (address?: IUserAddress) => {
        setSelectedAddress(address || null);
        setShowAddressForm(true);
    };

    const getAddresses = async () => {
        setLoading(true);
        try {
            const result = await searchAddress({ userId: findUserId() });
            if (result?.success) {
                setAddresses(result.data);
            } else {
                toast.error(result?.message || "Error fetching addresses");
            }
        } catch (error) {
            toast.error("Failed to fetch addresses");
        }
        setLoading(false);
    };

    const handleDelete = async (address: IUserAddress, index: number) => {
        if (window.confirm("Do you want to remove this address?")) {
            setLoading(true);
            try {
                const result = await deleteAAddress(address._id as string);
                if (result?.success) {
                    setAddresses(addresses.filter((_, i) => i !== index));
                    toast.success("Address removed successfully");
                } else {
                    toast.error(result?.message || "Error deleting address");
                }
            } catch (error) {
                toast.error("Failed to delete address");
            }
            setLoading(false);
        }
    };

    const handleDefaultAddress = async (address: IUserAddress) => {
        if (address.isDefaultAddress) return;

        if (window.confirm("Set as default address?")) {
            setLoading(true);
            try {
                const updatedAddress = { ...address, isDefaultAddress: true };
                const result = await updateAAddress(address._id as string, updatedAddress);
                if (result?.success) {
                    setAddresses(addresses.map(addr => ({
                        ...addr,
                        isDefaultAddress: addr._id === address._id
                    })));
                    toast.success("Default address updated");
                } else {
                    toast.error(result?.message || "Error updating default address");
                }
            } catch (error) {
                toast.error("Failed to update default address");
            }
            setLoading(false);
        }
    };

    React.useEffect(() => {
        getAddresses();
    }, []);

    return (
        <Box sx={{ mt: 3 }}>
            {!showAddressForm ? (
                <>
                    <Card sx={{ mb: 3 }}>
                        <Button
                            startIcon={<MdLocationPin />}
                            variant="contained"
                            fullWidth
                            onClick={() => handleAddressClick()}
                        >
                            Add New Address
                        </Button>
                    </Card>

                    {addresses.map((address) => (
                        <AddressCard
                            key={address._id}
                            address={address}
                            onEdit={() => handleAddressClick(address)}
                            onDelete={() => handleDelete(address, addresses.indexOf(address))}
                            onDefaultChecked={() => handleDefaultAddress(address)}
                        />
                    ))}
                </>
            ) : (
                <AddressForm
                    //@ts-ignore
                    data={selectedAddress}
                    onAddressAddOrUpdate={() => {
                        getAddresses(); // Refresh address list
                        handleAddressFormClose();
                    }}
                />
            )}
        </Box>

    );
};

export default AddressPage;
